import { Box, Button, Typography } from "@mui/material";
import { APIProvider } from "@vis.gl/react-google-maps";
import { CampaignType, NewCampaignContext } from "contexts/NewCampaign";
import { ReactNode, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  BentoBrandSearchParameters,
  Campaign,
  SavedBrandCollection,
} from "schemas/dashboard";
import { Map } from "schemas/functions";

import { API_KEY } from "components/LocationAutocomplete/useAutoCompletePrediction";
import BrandSearch from "features/Influencer/Campaigns/NewCampaign/SharedComponents/BrandSearch";
import PreviewCampaignBrands from "features/Influencer/Campaigns/NewCampaign/SharedComponents/PreviewCampaignBrands";

import StepNav from "../../StepNav";
import builderStyles from "../../styles";
import PreviousCampaignDialog from "./PreviousCampaignDialog";
import SavedListDialog from "./SavedListDialog";
import styles from "./styles";

type CampaignIntent = "new" | "repitch";

const INTENTS: {
  key: CampaignIntent;
  icon: string;
  title: string;
  description: string;
}[] = [
  {
    key: "new",
    icon: "fa-regular fa-sparkles",
    title: "New brands",
    description: "Reach out to brands you haven't contacted yet",
  },
  {
    key: "repitch",
    icon: "fa-regular fa-arrows-rotate",
    title: "Repitch",
    description: "Follow up with brands you've pitched before",
  },
];

const SAVED_LIST_SOURCE = {
  type: CampaignType.savedList,
  icon: "fa-regular fa-bookmark",
  title: "Saved list",
  description: "Choose from a list you've saved",
};
const BROWSE_BRANDS_SOURCE = {
  type: CampaignType.brandSearch,
  icon: "fa-regular fa-magnifying-glass",
  title: "Browse brands",
  description: "Search and filter all brands",
};
const PREVIOUS_CAMPAIGN_SOURCE = {
  type: CampaignType.previousCampaign,
  icon: "fa-regular fa-clock-rotate-left",
  title: "Previous campaign",
  description: "Repitch brands from a past campaign",
};

const SOURCES_BY_INTENT: Record<CampaignIntent, (typeof SAVED_LIST_SOURCE)[]> =
  {
    new: [SAVED_LIST_SOURCE, BROWSE_BRANDS_SOURCE],
    repitch: [
      SAVED_LIST_SOURCE,
      BROWSE_BRANDS_SOURCE,
      PREVIOUS_CAMPAIGN_SOURCE,
    ],
  };

interface Props {
  handleUpdateCampaign: (body: Map, skipMoveNext?: boolean) => void;
  moveBack: () => void;
  moveNext: () => void;
  submitLoading: boolean;
  errorMessage?: string;
  browseOnly?: boolean;
}

export default function Brands({
  handleUpdateCampaign,
  moveBack,
  moveNext,
  submitLoading,
  errorMessage,
  browseOnly,
}: Props) {
  const location = useLocation();
  const fromLocal =
    new URLSearchParams(location.search).get("from") === "local";

  const {
    campaign,
    selectedType,
    setSelectedType,
    selectAll,
    selectedBrandIds,
    excludedBrandIds,
    selectedCollection,
    setSelectedCollection,
    setSelectAll,
    setSelectedBrandIds,
    setExcludedBrandIds,
    currentSearchParams,
    setCurrentSearchParams,
    previousSelectionSnapshot,
    setPreviousSelectionSnapshot,
  } = useContext(NewCampaignContext);

  const [isReselecting, setIsReselecting] = useState(false);
  const [intent, setIntent] = useState<CampaignIntent | null>(null);
  const [sourceCampaign, setSourceCampaign] = useState<Campaign | null>(null);
  const [savedListDialogOpen, setSavedListDialogOpen] = useState(false);
  const [prevCampaignDialogOpen, setPrevCampaignDialogOpen] = useState(false);

  const hasBrands = Boolean(campaign?.campaignBrandCount);
  const showSummary = hasBrands && !isReselecting;

  const clearSelection = () => {
    setSelectAll(false);
    setSelectedBrandIds(new Set());
    setExcludedBrandIds(new Set());
  };

  const exitSource = () => {
    setSelectedType(null);
    setSelectedCollection(null);
    setSourceCampaign(null);
    clearSelection();
  };

  // In browse-only mode there is no source-picker to fall back to, so "back" from
  // the brand search either cancels a re-selection or steps back to Details.
  const handleBrowseBack = () => {
    if (isReselecting) {
      setIsReselecting(false);
    } else {
      moveBack();
    }
  };

  const startReselecting = () => {
    setPreviousSelectionSnapshot({
      selectedBrandIds: new Set(selectedBrandIds),
      excludedBrandIds: new Set(excludedBrandIds),
      selectAll,
      selectedType,
      selectedCollection,
      currentSearchParams,
    });
    clearSelection();
    setIsReselecting(true);
  };

  const revertSelection = () => {
    if (!previousSelectionSnapshot) return;
    setSelectedBrandIds(new Set(previousSelectionSnapshot.selectedBrandIds));
    setExcludedBrandIds(new Set(previousSelectionSnapshot.excludedBrandIds));
    setSelectAll(previousSelectionSnapshot.selectAll);
    setSelectedType(previousSelectionSnapshot.selectedType);
    setSelectedCollection(previousSelectionSnapshot.selectedCollection);
    setCurrentSearchParams(previousSelectionSnapshot.currentSearchParams);
    setSourceCampaign(null);
    setIntent(null);
    setIsReselecting(false);
  };

  const handleSourceClick = (type: CampaignType) => {
    if (type === CampaignType.savedList) {
      setSavedListDialogOpen(true);
    } else if (type === CampaignType.previousCampaign) {
      setPrevCampaignDialogOpen(true);
    } else {
      clearSelection();
      setSelectedCollection(null);
      setSourceCampaign(null);
      setSelectedType(CampaignType.brandSearch);
    }
  };

  const handlePickCollection = (collection: SavedBrandCollection) => {
    clearSelection();
    setSourceCampaign(null);
    setSelectedCollection(collection);
    setSelectedType(CampaignType.savedList);
    setSavedListDialogOpen(false);
  };

  const handlePickCampaign = (sourceCampaignChoice: Campaign) => {
    clearSelection();
    setSelectedCollection(null);
    setSourceCampaign(sourceCampaignChoice);
    setSelectedType(CampaignType.previousCampaign);
    setPrevCampaignDialogOpen(false);
  };

  const handleSubmit = async (
    convertedSearchParams?: BentoBrandSearchParameters,
    numberSelected?: number,
  ) => {
    if (!isReselecting && hasBrands) {
      moveNext();
      return;
    }
    const body: Map = {};
    if (selectedBrandIds.size > 0)
      body.bentoBrandIds = Array.from(selectedBrandIds);
    if (selectAll && excludedBrandIds.size > 0) {
      body.excludedBentoBrandIds = Array.from(excludedBrandIds);
    }
    if (numberSelected) body.perPage = numberSelected;
    body.searchParameters = convertedSearchParams;
    body.isRepitching = intent === "repitch";
    await handleUpdateCampaign(body, true);
    setSelectedType(null);
    setSourceCampaign(null);
    setIntent(null);
    setIsReselecting(false);
    setPreviousSelectionSnapshot(null);
  };

  const handlePickerBack = () => {
    if (intent) {
      clearSelection();
      setSelectedCollection(null);
      setSourceCampaign(null);
      setIntent(null);
      return;
    }
    if (isReselecting) {
      setIsReselecting(false);
      return;
    }
    moveBack();
  };

  const renderSourceCard = (
    type: CampaignType,
    icon: string,
    title: string,
    description: string,
  ) => (
    <Box
      key={type}
      onClick={() => handleSourceClick(type)}
      sx={styles.sourceCard}
    >
      <Box sx={styles.sourceIcon}>
        <Box
          component="i"
          className={icon}
          sx={{ fontSize: 18, color: "primary.main" }}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );

  const revertAction =
    isReselecting && previousSelectionSnapshot ? (
      <Button sx={styles.secondaryButton} onClick={revertSelection}>
        <Box
          component="i"
          className="fa-regular fa-arrow-rotate-left"
          sx={{ mr: 1 }}
        />
        Revert to {campaign?.campaignBrandCount ?? 0} previously selected
      </Button>
    ) : null;

  // A source is chosen (or browse-only mode skips the picker) → render the brand
  // search. Browse-only goes straight here instead of showing intent/source cards.
  if (selectedType || (browseOnly && !showSummary)) {
    const savedBrandCollectionId =
      selectedType === CampaignType.savedList &&
      selectedCollection &&
      selectedCollection.id > 0
        ? selectedCollection.id
        : undefined;
    const isAllSavedCollection =
      selectedType === CampaignType.savedList &&
      Boolean(selectedCollection && selectedCollection.id <= 0);
    const sourceName =
      selectedType === CampaignType.savedList
        ? selectedCollection?.name
        : selectedType === CampaignType.previousCampaign
          ? sourceCampaign?.name
          : null;
    const sourceFallback =
      selectedType === CampaignType.savedList ? "this list" : "this campaign";
    const nameNode = (
      <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
        {sourceName ? `“${sourceName}”` : sourceFallback}
      </Box>
    );

    let banner: ReactNode = null;
    if (intent === "repitch") {
      const scope =
        selectedType === CampaignType.savedList ? (
          <> in {nameNode}</>
        ) : selectedType === CampaignType.previousCampaign ? (
          <> from {nameNode}</>
        ) : null;
      banner = (
        <>
          Showing brands{scope} you last reached out to over 30 days ago who
          never replied.
        </>
      );
    } else if (selectedType === CampaignType.savedList) {
      banner = (
        <>
          Showing unsent brands in {nameNode}. Brands without contacts
          won&apos;t appear here.
        </>
      );
    }

    return (
      <Box sx={builderStyles.card}>
        {banner && (
          <Box sx={styles.infoChip}>
            <Box
              component="i"
              className="fa-regular fa-circle-info"
              sx={{ fontSize: 12, color: "primary.main", mr: 0.75 }}
            />
            <Box component="span">{banner}</Box>
          </Box>
        )}
        <APIProvider apiKey={API_KEY || ""}>
          <BrandSearch
            repitching={intent === "repitch"}
            savedBrandCollectionId={savedBrandCollectionId}
            isAllSavedCollection={isAllSavedCollection}
            sourceCampaignId={
              selectedType === CampaignType.previousCampaign
                ? sourceCampaign?.id
                : undefined
            }
            onNext={handleSubmit}
            nextLoading={submitLoading}
            onBack={browseOnly ? handleBrowseBack : exitSource}
            secondaryAction={revertAction}
            disableAutoScroll={browseOnly}
            sourceLabel={sourceName ?? undefined}
          />
        </APIProvider>
      </Box>
    );
  }

  return (
    <Box sx={builderStyles.formColumn}>
      <Box sx={builderStyles.card}>
        {showSummary ? (
          <>
            <Box sx={styles.summaryRow}>
              <Box sx={styles.selectedPill}>
                <Box
                  component="i"
                  className="fa-regular fa-circle-check"
                  sx={{ fontSize: 18, color: "primary.main" }}
                />
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                  {campaign?.campaignBrandCount} brand
                  {campaign?.campaignBrandCount === 1 ? "" : "s"} selected
                </Typography>
              </Box>
              {!fromLocal && (
                <Button sx={styles.secondaryButton} onClick={startReselecting}>
                  Change selection
                </Button>
              )}
            </Box>
            <Box
              sx={{
                maxHeight: "calc(100vh - 360px)",
                minHeight: 240,
                overflowY: "auto",
              }}
            >
              <PreviewCampaignBrands />
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={builderStyles.cardTitle}>
                {intent
                  ? "Where should we find them?"
                  : "Who are you pitching?"}
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}
              >
                {intent
                  ? "Pick a source for this campaign's brands."
                  : "Choose whether to reach new brands or repitch past ones."}
              </Typography>
            </Box>

            <Box sx={styles.sourceGrid}>
              {!intent
                ? INTENTS.map(({ key, icon, title, description }) => (
                    <Box
                      key={key}
                      onClick={() => {
                        clearSelection();
                        setSelectedCollection(null);
                        setSourceCampaign(null);
                        setIntent(key);
                      }}
                      sx={styles.sourceCard}
                    >
                      <Box sx={styles.sourceIcon}>
                        <Box
                          component="i"
                          className={icon}
                          sx={{ fontSize: 18, color: "primary.main" }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                          {title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "text.secondary",
                            mt: 0.25,
                          }}
                        >
                          {description}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                : SOURCES_BY_INTENT[intent].map(
                    ({ type, icon, title, description }) =>
                      renderSourceCard(type, icon, title, description),
                  )}
            </Box>
          </>
        )}
      </Box>

      <StepNav
        onBack={handlePickerBack}
        backLabel={isReselecting && !intent ? "Cancel" : "Back"}
        onNext={() => handleSubmit()}
        nextDisabled={!showSummary || Boolean(errorMessage)}
        nextLoading={submitLoading}
        secondaryAction={revertAction}
      />

      <SavedListDialog
        open={savedListDialogOpen}
        onClose={() => setSavedListDialogOpen(false)}
        onSelect={handlePickCollection}
      />
      <PreviousCampaignDialog
        open={prevCampaignDialogOpen}
        onClose={() => setPrevCampaignDialogOpen(false)}
        onSelect={handlePickCampaign}
        currentCampaignId={campaign?.id}
      />
    </Box>
  );
}
